/**
 * Offline Banner Component
 * Shows network status and offline mode indicators
 */

import React from 'react';
import { WifiOff, Wifi, AlertCircle } from 'lucide-react';
import { useOfflineDetection } from '@/hooks/useOfflineDetection';
import { useI18nStore } from '@/stores/i18nStore';

export const OfflineBanner: React.FC = () => {
  const { isOnline, isSlowConnection, connectionType, wasOffline } = useOfflineDetection();
  const { t } = useI18nStore();

  // Show "Back Online" message briefly when reconnecting
  if (wasOffline && isOnline) {
    return (
      <div className="fixed top-16 left-0 right-0 z-40 animate-slide-down">
        <div className="bg-green-600 text-white px-4 py-2 shadow-lg">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
            <Wifi className="w-5 h-5" />
            <span className="text-sm font-medium">
              {t.connectionRestoredSyncing}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Show offline banner when offline
  if (!isOnline) {
    return (
      <div className="fixed top-16 left-0 right-0 z-40 animate-slide-down">
        <div className="bg-amber-600 text-white px-4 py-2 shadow-lg">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
            <WifiOff className="w-5 h-5" />
            <span className="text-sm font-medium">
              {t.offlineModeDescription}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Show slow connection warning
  if (isSlowConnection) {
    return (
      <div className="fixed top-16 left-0 right-0 z-40 animate-slide-down">
        <div className="bg-orange-600 text-white px-4 py-2 shadow-lg">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm font-medium">
              {t.slowConnectionDescription.replace('({connectionType})', `(${connectionType})`)}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default OfflineBanner;
